import React from 'react';
import Badge, { getStatusColors } from '../ui/Badge';
import { formatShortDate, daysBetween } from '../../utils/date-utils';
import {
  ClipboardList, Wrench, Truck, Package, Hammer, MapPin, CalendarDays,
  LayoutGrid, Frame, Receipt, ClipboardCheck,
} from 'lucide-react';

const INK   = '#2B2620';
const STONE = '#8C8275';

// Same keys/icons as the popup's DOC_TABS (JobDetailView.jsx) for consistency.
const DOC_BADGES = [
  { key: 'cutlist',    label: 'Cut List',    Icon: ClipboardList },
  { key: 'estimate',   label: 'Estimate',    Icon: Receipt },
  { key: 'joborder',   label: 'Job Order',   Icon: ClipboardCheck },
  { key: 'shoporder',  label: 'Shop Order',  Icon: Wrench },
  { key: 'glassorder', label: 'Glass Order', Icon: LayoutGrid },
  { key: 'flexscreen', label: 'Flex Screen', Icon: Frame },
];

function DocBadges({ pdfUrls, contractNumber, notes, accent }) {
  // pdfUrls comes straight off the job doc already in memory — savePDFUrlToJob
  // writes the same map onto both jobs/{id} and cutlistJobs/{id}, so there's
  // no need for a per-card Firestore read just to show these badges.
  const pdfKeys = Object.keys(pdfUrls || {}).filter(k => pdfUrls[k]);
  // Estimate + Job Order aren't stored files (the popup regenerates them live),
  // but every job in Production got here via an approved Job Order — that's
  // the only path a jobs/{id} doc is ever created — so both are always real
  // for any job with a contract number. No fetch needed to know that.
  const guaranteed = contractNumber ? ['estimate', 'joborder'] : [];
  const available = DOC_BADGES.filter(d => pdfKeys.includes(d.key) || guaranteed.includes(d.key));

  // If any docs are available, show icon badges
  if (available.length > 0) {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', flexShrink: 0, maxWidth: '96px', justifyContent: 'flex-end' }}>
        {available.map(d => (
          <div key={d.key} title={d.label} style={{
            width: '22px', height: '22px', borderRadius: '6px', flexShrink: 0,
            background: accent.bg, border: '1px solid var(--cell-border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <d.Icon size={12} strokeWidth={2.25} color={accent.color} />
          </div>
        ))}
      </div>
    );
  }

  // No PDFs — show notes preview if available (ordered job: ProVia, Vector, Minnkota, etc.)
  if (notes && notes.trim()) {
    const preview = notes.trim().substring(0, 12).trim();
    return (
      <div style={{
        padding: '3px 8px', borderRadius: '6px', fontSize: '9px', fontWeight: '700',
        background: 'rgba(139,69,19,0.08)', color: 'var(--brand-brown)', border: '1px solid rgba(139,69,19,0.15)',
        whiteSpace: 'nowrap', lineHeight: '1.4', textAlign: 'center', maxWidth: '80px',
        overflow: 'hidden', textOverflow: 'ellipsis'
      }}>
        {preview}
      </div>
    );
  }

  return null;
}

export default function JobsCardView({ jobs = [], onJobClick }) {
  if (jobs.length === 0) {
    return (
      <div style={{
        padding: '60px 20px',
        textAlign: 'center'
      }}>
        <ClipboardList size={40} strokeWidth={1.5} color={STONE} style={{ marginBottom: '16px', opacity: 0.6 }} />

        <div style={{
          fontSize: '16px',
          fontWeight: '600',
          color: INK,
          marginBottom: '8px'
        }}>
          No Jobs Found
        </div>

        <div style={{
          fontSize: '14px',
          color: STONE
        }}>
          Try adjusting your filters
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: '16px'
    }}>
      {jobs.map(job => {
        const getIcon = () => {
          if (job.isRepair) return { Icon: Wrench, color: 'var(--tag-orange-text)' };
          if (job.isDeliveryOnly) return { Icon: Truck, color: 'var(--tag-green-text)' };
          if (job.isPickupAtShop) return { Icon: Package, color: 'var(--tag-blue-text)' };
          return { Icon: Hammer, color: 'var(--tag-blue-text)' };
        };

        const getAgeColor = () => {
          const days = daysBetween(job.soldDate, new Date());
          if (days <= 7) return 'var(--status-text-bold-green)';
          if (days <= 21) return 'var(--tag-orange-text)';
          return 'var(--status-text-bold-red)';
        };

        const icon = getIcon();
        const ageColor = getAgeColor();
        const ageDays = daysBetween(job.soldDate, new Date());
        // No cutlistJobId = nothing TWP-built on this job — it's supplier-ordered
        // windows/doors only. Flagged with a dashed (vs solid) accent below —
        // color itself tracks the job's stage, same as its status pill.
        const isOrdered = !job.cutlistJobId;
        const sc = getStatusColors(job.status || 'sold');

        return (
          <div
            key={job.id}
            onClick={() => onJobClick(job)}
            style={{
              padding: '16px',
              background: 'var(--job-card-bg)',
              borderRadius: '12px',
              border: '1px solid var(--cell-border)',
              borderRight: `4px ${isOrdered ? 'dashed' : 'solid'} ${sc.color}`,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              marginBottom: '12px'
            }}>
              {/* Icon */}
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: `${icon.color}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <icon.Icon size={21} strokeWidth={2} color={icon.color} />
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  marginBottom: '4px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {job.customerName || job.name}
                </div>

                {job.contractNumber && (
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: 'var(--brand-brown)',
                    marginBottom: '4px'
                  }}>
                    #{job.contractNumber}
                  </div>
                )}

                {job.name && (
                  <div style={{
                    fontSize: '12px',
                    color: 'var(--tag-blue-text)',
                    marginBottom: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <MapPin size={11} strokeWidth={2} />
                    <span>{job.name}</span>
                  </div>
                )}

                <div style={{
                  fontSize: '12px',
                  color: 'var(--text-secondary)',
                  marginBottom: '6px'
                }}>
                  {job.jobType || job.status?.replace(/_/g, ' ')}
                </div>

                <Badge status={job.status || 'sold'}>
                  {job.status || 'sold'}
                </Badge>
              </div>

              {/* PDF badges or notes preview — top-right */}
              <DocBadges pdfUrls={job.pdfUrls} contractNumber={job.contractNumber} notes={job.notes} accent={sc} />
            </div>

            {/* Dates */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              marginBottom: '12px',
              fontSize: '12px',
              color: 'var(--text-secondary)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><CalendarDays size={12} strokeWidth={2} /> Sold: {formatShortDate(job.soldDate)}</div>
              {job.scheduledDate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Wrench size={12} strokeWidth={2} /> Install: {formatShortDate(job.scheduledDate)}</div>
              )}
            </div>

            {/* Job Details */}
            {(job.windowCount > 0 || job.doorCount > 0 || job.durationDays) && (
              <div style={{
                display: 'flex',
                gap: '12px',
                fontSize: '11px',
                color: 'var(--text-tertiary)',
                marginBottom: '12px',
                flexWrap: 'wrap'
              }}>
                {job.windowCount > 0 && <span>{job.windowCount} windows</span>}
                {job.doorCount > 0 && <span>{job.doorCount} doors</span>}
                {job.durationDays && <span>{job.durationDays} days</span>}
              </div>
            )}

            {/* Age Indicator */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              background: `${ageColor}15`,
              borderRadius: '8px'
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: ageColor
              }} />
              <span style={{
                fontSize: '12px',
                fontWeight: '600',
                color: ageColor
              }}>
                Age: {ageDays} days
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}